import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { config, MdEditor, ToolbarNames } from 'md-editor-rt'
import RU from '@vavt/cm-extension/dist/locale/ru'
import 'md-editor-rt/lib/style.css'
import { collectionsApi } from 'api/collections'
import { showServerError } from 'shared/utils/showServerError'
import noProductImage from 'assets/images/no-image-product.png'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { EndDateField, FixedPositionFields } from 'components/ui'
import { toEndDateField } from 'shared/utils/endDate'
import {
  Button,
  Flex,
  Form,
  Input,
  message,
  Popconfirm,
  Skeleton,
  Table,
  Typography,
  Switch,
  Upload
} from 'antd'
import { TextOnePhoto } from 'components/ui/Uploader/TextOnePhoto'
import { IMAGES_URL } from 'shared/constants/urls'
import {
  DeleteFilled,
  PlusOutlined,
  UploadOutlined,
  PaperClipOutlined,
  CloseCircleFilled
} from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { IProductResponse } from 'models/product'
import { ECollectionStatus } from 'models/collections'
import { getRoute } from 'shared/constants/routes'
import { GetProductModal } from 'components/GetProductModal'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { CREATE_MODE } from './utils/constants'
import { HeaderEdit } from './components/HeaderEdit'
import {
  getEditCollectionSubmitData,
  getCreateCollectionSubmitData
} from './utils/getSubmitData'

const toolbars: ToolbarNames[] = [
  'bold',
  'underline',
  'italic',
  '-',
  'strikeThrough',
  'title',
  'sub',
  'sup',
  'quote',
  'unorderedList',
  'orderedList',
  '-',
  'table',
  '-',
  'revoke',
  'next',
  'save',
  '=',
  'pageFullscreen',
  'fullscreen',
  'preview',
  'htmlPreview'
]

config({
  editorConfig: {
    languageUserDefined: {
      ru: RU
    }
  }
})

export const CollectionEdit = () => {
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()
  const isCreate = id === CREATE_MODE
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [visibleProductModal, setVisibleProductModal] = useState(false)
  const [products, setProducts] = useState<IProductResponse[]>([])

  const getCollection = async () => {
    if (!id || isCreate) return
    try {
      const response = await collectionsApi.getCollection(id)
      const { data } = response
      form.setFields([
        { name: 'title', value: data.title },
        { name: 'desc', value: data.desc },
        { name: 'is_thematic', value: data.is_thematic },
        { name: 'fixed_position', value: !!data.fixed_position },
        { name: 'end_date', value: toEndDateField(data.end_date) }
      ])
      if (data.photo) {
        form.setFieldValue('photo', [
          {
            uid: data.photo,
            thumbUrl: `${IMAGES_URL}${data.photo}`
          }
        ])
      }
      if (data.icon) {
        form.setFieldValue('icon', {
          file: {
            name: data.icon
          }
        })
      }
      setProducts(data.products)
    } catch (e) {
      showServerError(e)
    }
  }

  const handleDeleteProduct = (id: string) => {
    const updatedCollection = products.filter((p) => p._id !== id)
    setProducts(updatedCollection)
  }

  const columns: ColumnsType<IProductResponse> = [
    {
      title: '',
      dataIndex: 'number',
      key: ''
    },
    {
      title: 'Фото',
      dataIndex: 'photo',
      width: 76,
      render: (_, { _id, photo }) => {
        return photo && photo.length ? (
          <div
            style={{
              width: 62,
              height: 56,
              backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ) : (
          <img
            src={noProductImage}
            alt='Изображение товара'
            width={62}
            height={56}
          />
        )
      }
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Категория',
      dataIndex: '',
      key: 'category_id'
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      render: (_, { price }) => (
        <span>{price ? getNormalizedPrice(price) : '-'}</span>
      )
    },
    {
      title: 'operation',
      dataIndex: 'operation',
      render: (_, record) =>
        products.length >= 1 ? (
          <Popconfirm
            title='Удалить?'
            onConfirm={() => handleDeleteProduct(record._id)}
          >
            <Button
              type='primary'
              danger
              icon={<DeleteFilled />}
              size={'small'}
            />
          </Popconfirm>
        ) : null
    }
  ]

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true)
        await getCollection()
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }
    getData()
  }, [])

  const onSubmit = async (status: ECollectionStatus) => {
    if (!id) return null
    try {
      const formValues = form.getFieldsValue()
      const { _id, ...values } = formValues
      if (!formValues.photo) {
        message.error('Добавьте изображение')
        return null
      }
      if (values.is_thematic && !values.icon) {
        message.error('Добавьте иконку для тематической категории')
        return null
      }
      await form.validateFields()
      if (!products.length) {
        message.error('Добавьте товар')
        return null
      }
      setIsSubmitting(true)
      const body = { ...values, status, products }
      if (isCreate) {
        await collectionsApi.createCollection(
          getCreateCollectionSubmitData(body)
        )
        message.success('Подборка успешно добавлена')
      } else {
        await collectionsApi.updateCollection(
          id,
          getEditCollectionSubmitData(body)
        )
        message.success('Подборка успешно обновлена')
      }
    } catch (e) {
      const error = e as AxiosError
      if (error.response?.status) {
        showServerError(e)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCloseProductModal = () => {
    setVisibleProductModal(false)
  }

  const saveProducts = (newProducts: IProductResponse[]) => {
    const existingIds = new Set(products.map((p) => p._id))
    const filteredNewElements = newProducts.filter(
      (newItem) => !existingIds.has(newItem._id)
    )
    const items = [...products, ...filteredNewElements]
    setProducts(items)
  }

  if (isLoading) {
    return (
      <Flex gap='middle' vertical>
        <Skeleton.Input active size={'large'} block />
        <Flex gap='middle'>
          <Skeleton.Image active />
          <Skeleton.Input active size={'large'} block />
        </Flex>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Flex>
    )
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <Form form={form} name='sellers' size='large' autoComplete='off'>
        <HeaderEdit
          isSubmitting={isSubmitting}
          onPublish={() => onSubmit(ECollectionStatus.PUBLISHED)}
          onArchive={() => onSubmit(ECollectionStatus.ARCHIVED)}
          isCreate={isCreate}
        />
        <Flex align='center' gap={16} style={{ marginBottom: 24 }}>
          <Form.Item
            name='is_thematic'
            valuePropName='checked'
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Typography.Text strong style={{ fontSize: 16 }}>
            Создать тематическую категорию
          </Typography.Text>
        </Flex>
        <FixedPositionFields />
        <EndDateField subject='подборка' />
        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.is_thematic !== currentValues.is_thematic
          }
          noStyle
        >
          {({ getFieldValue }) =>
            getFieldValue('is_thematic') ? (
              <Flex vertical gap={14} style={{ marginBottom: 24 }}>
                <Typography.Text strong style={{ fontSize: 16 }}>
                  Иконка тематической категории в меню:
                </Typography.Text>
                <Flex vertical gap={14}>
                  <Flex align='center' gap={20}>
                    <Form.Item name='icon' style={{ marginBottom: 0 }}>
                      <Upload
                        accept='.svg'
                        maxCount={1}
                        showUploadList={false}
                        beforeUpload={(file) => {
                          const isSvg = file.type === 'image/svg+xml'
                          if (!isSvg) {
                            message.error(
                              'Вы можете загрузить только файл SVG!'
                            )
                          }
                          return isSvg || Upload.LIST_IGNORE
                        }}
                        onChange={(info) => {
                          if (info.file.status === 'done') {
                            message.success(
                              `${info.file.name} успешно загружен`
                            )
                            form.setFieldsValue({ icon: info })
                          } else if (info.file.status === 'error') {
                            message.error(`${info.file.name} ошибка загрузки.`)
                          }
                        }}
                        customRequest={({ file, onSuccess }) => {
                          setTimeout(() => {
                            onSuccess && onSuccess('ok')
                          }, 0)
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          style={{
                            borderRadius: '6px',
                            width: '295px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                    <Typography.Text
                      type='secondary'
                      style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.65)' }}
                    >
                      Только SVG icon
                    </Typography.Text>
                  </Flex>

                  <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                      return prevValues.icon !== currentValues.icon
                    }}
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const iconInfo = getFieldValue('icon')
                      const hasFile =
                        iconInfo &&
                        (iconInfo.file ||
                          (iconInfo.fileList && iconInfo.fileList.length > 0))

                      return hasFile ? (
                        <Flex
                          gap={6}
                          align='center'
                          style={{ marginLeft: '0px' }}
                        >
                          <Flex gap={8} align='center'>
                            <PaperClipOutlined
                              style={{
                                color: 'rgba(0, 0, 0, 0.45)',
                                fontSize: '14px'
                              }}
                            />
                            <Typography.Text
                              style={{ color: '#1677FF', fontSize: '14px' }}
                            >
                              {(iconInfo.file && iconInfo.file.name) ||
                                (iconInfo.fileList &&
                                  iconInfo.fileList[0] &&
                                  iconInfo.fileList[0].name) ||
                                'icon.svg'}
                            </Typography.Text>
                          </Flex>
                          <CloseCircleFilled
                            style={{
                              color: '#1677FF',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onClick={() => {
                              form.setFieldsValue({ icon: undefined })
                            }}
                          />
                        </Flex>
                      ) : null
                    }}
                  </Form.Item>
                </Flex>
              </Flex>
            ) : null
          }
        </Form.Item>

        <Flex gap={32}>
          <div style={{ width: '320px', flexShrink: 0 }}>
            <Typography.Title level={5} style={{ marginBottom: 24 }}>
              Обложка для подборки
            </Typography.Title>
            <Uploader
              name='photo'
              type='variation'
              rules={[{ required: true, message: 'Добавьте изображение' }]}
            />
            <TextOnePhoto />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Form.Item
              name='title'
              label='Название подборки'
              rules={[
                { required: true, message: 'Добавьте название подборки' }
              ]}
              style={{ width: '100%' }}
            >
              <Input placeholder='' />
            </Form.Item>
            <Typography.Title level={5}>Описание</Typography.Title>
            <Form.Item name='desc' valuePropName='modelValue'>
              <MdEditor
                language='ru'
                noUploadImg
                style={{ height: '400px' }}
                toolbars={toolbars}
              />
            </Form.Item>
          </div>
        </Flex>

        <Flex style={{ marginTop: 32, marginBottom: 16 }} justify='end' gap={8}>
          <Popconfirm
            title='Очистить подборку?'
            onConfirm={() => setProducts([])}
            okText='Да'
            cancelText='Нет'
            disabled={!products.length}
          >
            <Button disabled={!products.length}>Очистить</Button>
          </Popconfirm>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => setVisibleProductModal(true)}
          >
            Добавить товар
          </Button>
        </Flex>

        <Table
          showHeader={false}
          pagination={false}
          columns={columns}
          dataSource={products}
          rowKey={(el) => el._id}
          rowClassName='tableRow'
        />

        <GetProductModal
          onSave={saveProducts}
          visible={visibleProductModal}
          onClose={onCloseProductModal}
        />
      </Form>
    </div>
  )
}
